import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { ArticleCard } from '../components/ArticleCard';
import { Skeleton } from '../components/ui/skeleton';
import { Library, ArrowLeft } from 'lucide-react';
import { API, authFetch } from '../lib/api';

const SeriesPage = () => {
  const { seriesId } = useParams();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSeries = async () => {
      setLoading(true);
      try {
        const response = await authFetch(`${API}/series/${seriesId}`);
        if (response.ok) {
          setSeries(await response.json());
        }
      } catch (error) {
        console.error('Failed to fetch series:', error);
      }
      setLoading(false);
    };
    fetchSeries();
  }, [seriesId]);

  return (
    <div className="min-h-screen bg-background" data-testid="series-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        {loading ? (
          <>
            <Skeleton className="h-8 w-64 mb-3" />
            <Skeleton className="h-4 w-96 mb-8" />
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="py-6">
                <Skeleton className="h-6 w-full mb-2" />
                <Skeleton className="h-4 w-48" />
              </div>
            ))}
          </>
        ) : series ? (
          <>
            <div className="flex items-center gap-3 mb-2">
              <Library className="w-6 h-6 text-accent" />
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Fraunces, serif' }}>
                {series.title}
              </h1>
            </div>
            {series.description && (
              <p className="text-muted-foreground mb-8">{series.description}</p>
            )}
            <p className="text-sm text-muted-foreground mb-6">
              {series.articles?.length || 0} article{series.articles?.length !== 1 ? 's' : ''} in this series
            </p>

            <div className="divide-y divide-border">
              {series.articles?.map((article, index) => (
                <div key={article.article_id} className="relative">
                  <span className="absolute left-0 top-8 text-xs font-mono text-muted-foreground/50">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="pl-8">
                    <ArticleCard article={article} />
                  </div>
                </div>
              ))}
            </div>

            {(!series.articles || series.articles.length === 0) && (
              <div className="py-16 text-center">
                <Library className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No articles in this series yet.</p>
              </div>
            )}
          </>
        ) : (
          <div className="py-16 text-center">
            <p className="text-muted-foreground">Series not found.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default SeriesPage;
